import React, { ReactChild } from 'react'
import { shuffle } from '@elgatoylacaja/utils'
import { EVP, EVPKey, EVPs, EVPRange } from '../data'
import { zip } from 'd3'

export type LoginServices = 'email' | 'facebook' | 'google'

type User = {
  accessToken: string
  experimentDone: boolean
  allowsNews?: boolean
  prevAllowsNews: boolean | null
}

export type AppContextData = {
  datetime: string
  auth: boolean
  visited: Array<string>
  user: User
  experiment: Experiment
  submitted: boolean
  regressors?: Regressors
}

type Regressors = {
  edad: number | string
  genero: string
  nivel_educativo: string
  pais: string
  provincia: string
}

export type Etapa1Data = {
  order: EVP[]
  valores_exactos?: Record<EVPKey, number>
  timestamps: Record<string, number>
  rangos?: Record<EVPRange, [number, number]>
}

type Etapa2OptionConfig = {
  keys: EVPKey[]
  labels: string[]
  values: number[]
  type: 'evps' | 'numeros'
  number: number
}

type Etapa2Values = {
  evps_1: number
  evps_2: number
  evps_3: number
  evps_4: number
  numeros_1: number
  numeros_2: number
  numeros_3: number
  numeros_4: number
}

export type Etapa2Data = {
  order: Etapa2OptionConfig[]
  values?: Etapa2Values
}

export type Etapa3Data = {
  plays: Level[]
  fps: number
  width: number
  dificultades: typeof dificultades
  puntos?: number
}

export type Experiment = {
  fork_etapa_3:
    | 'numeros-numeros-numeros'
    | 'palabras-palabras-numeros'
    | 'palabras-palabras-palabras'
  etapa_1: Etapa1Data
  etapa_2: Etapa2Data
  etapa_3: Etapa3Data
  timestamps: {
    inicio_experimento: number
    inicio_etapa_1: number
    fin_etapa_1: number
    inicio_etapa_2: number
    fin_etapa_2: number
    inicio_etapa_3: number
    fin_etapa_3: number
    inicio_regresores: number
    fin_regresores: number
  }
}

type LevelKey =
  | 'azul_d4'
  | 'azul_d3'
  | 'azul_d2'
  | 'azul_d1'
  | 'rojo_d4'
  | 'rojo_d3'
  | 'rojo_d2'
  | 'rojo_d1'

type Point = [number, number]

type Color = '1' | '2'

export type OwnFormData = {
  color: Color
  probabilidad: number | string
  timestamps: {
    color: string[]
    probabilidad: string[]
  }
}

export type OthersFormData = {
  other_color: Color
  other_probabilidad: number
  color?: Color
  probabilidad?: number | string
  random: boolean
  timestamps: {
    color: string[]
    probabilidad: string[]
  }
}

export type Level = {
  step: number
  level_key: LevelKey
  red_points: Point[]
  blue_points: Point[]
  own_form?: OwnFormData
  others_form?: OthersFormData
}

const { min, round, random, sqrt, cos, sin, PI, abs } = Math

const width = min(300, window.innerWidth * 0.8, window.innerHeight * 0.4)
const R = width / 2
const dotRadius = round(width * 0.02)

const fork_etapa_3 = shuffle([
  'numeros-numeros-numeros',
  'palabras-palabras-numeros',
  'palabras-palabras-palabras',
])[0] as
  | 'numeros-numeros-numeros'
  | 'palabras-palabras-numeros'
  | 'palabras-palabras-palabras'

export const dificultades = {
  d1: 21,
  d2: 22,
  d3: 24,
  d4: 28,
}

export const levels: Level[] = shuffle<LevelKey>([
  'azul_d4',
  'azul_d3',
  'azul_d2',
  'azul_d1',
  'rojo_d4',
  'rojo_d3',
  'rojo_d2',
  'rojo_d1',
]).map((level_key, i) => {
  const puntos: Point[] = []
  const [colorGanador, dificultad] = level_key.split('_')
  const puntosGanador = dificultades[dificultad as keyof typeof dificultades]

  const cantPuntosRojos =
    colorGanador === 'rojo' ? puntosGanador : 40 - puntosGanador
  const cantPuntosAzules = 40 - cantPuntosRojos

  const generarPunto = (): Point => {
    let a = random() * 2 * PI
    let r = (R - dotRadius) * sqrt(random())

    let x = round(R + r * cos(a))
    let y = round(R + r * sin(a))
    return [x, y]
  }

  const hayPuntoPegado = (punto: Point, puntos: Point[]) => {
    const [x, y] = punto
    const puntoPegado = puntos.find(
      ([X, Y]) => abs(x - X) < dotRadius * 2 && abs(y - Y) < dotRadius * 2
    )
    return puntoPegado !== undefined
  }

  const red_points = Array.from({ length: cantPuntosRojos }, () => {
    let punto
    do {
      punto = generarPunto()
    } while (hayPuntoPegado(punto, puntos))
    puntos.push(punto)
    return punto
  })

  const blue_points = Array.from({ length: cantPuntosAzules }, () => {
    let punto
    do {
      punto = generarPunto()
    } while (hayPuntoPegado(punto, puntos))
    puntos.push(punto)
    return punto
  })

  return {
    step: i + 1,
    level_key,
    blue_points,
    red_points,
  }
})

const initialAppContextData: () => AppContextData = () => {
  const shuffled_evps = shuffle(EVPs)
  const evps_1 = shuffled_evps.slice(0, EVPs.length / 2)
  const evps_2 = shuffled_evps.slice(EVPs.length / 2, EVPs.length)
  const evps_combinados = shuffle(zip(evps_1, evps_2))

  const etapa2_order = shuffle([
    ...evps_combinados.slice(0, EVPs.length / 4).map((x, i) => ({
      keys: x.map(_ => _.key),
      labels: x.map(_ => _.palabra),
      values: x.map(_ => _.id),
      type: 'evps',
      number: i + 1,
    })),
    ...evps_combinados.slice(EVPs.length / 4, EVPs.length / 2).map((x, i) => ({
      keys: x.map(_ => _.key),
      labels: x.map(_ => _.palabra),
      values: x.map(_ => _.id),
      type: 'numeros',
      number: i + 1,
    })),
  ])

  return {
    datetime: new Date().toLocaleString('es', { hour12: false }),
    auth: false,
    visited: [],
    experiment: {
      fork_etapa_3,
      timestamps: {
        inicio_experimento: new Date().getTime(),
        inicio_etapa_1: 0,
        fin_etapa_1: 0,
        inicio_etapa_2: 0,
        fin_etapa_2: 0,
        inicio_etapa_3: 0,
        fin_etapa_3: 0,
        inicio_regresores: 0,
        fin_regresores: 0,
      },
      etapa_1: {
        order: shuffle(EVPs),
        // rangos: {
        //   casi_seguro: [0, 100],
        //   creo_que_si: [20, 80],
        //   dificilmente: [30, 70],
        //   igual_al_azar: [45, 55],
        //   lo_dudo: [40, 60],
        //   muy_probable: [15, 85],
        //   no_se: [30, 45],
        //   puede_ser: [20, 24],
        //   quizas: [2, 98],
        //   seguro: [98, 100]
        // },
        timestamps: {},
      },
      etapa_2: {
        order: etapa2_order as Etapa2OptionConfig[],
      },
      etapa_3: {
        plays: levels,
        fps: -999,
        width,
        dificultades,
      },
    },
    user: {
      accessToken: '',
      allowsNews: false,
      experimentDone: false,
      prevAllowsNews: null,
      service: 'email',
    },
    submitted: false,
  }
}

type Omit<T, K extends string | number | symbol> = {
  [P in Exclude<keyof T, K>]: T[P]
}

type Login = {
  type: 'LOGIN'
  user: Omit<User, 'allowsNews'>
}

type Visit = {
  type: 'REGISTER_VISIT'
  route: string
}

type AcceptTerms = {
  type: 'ACCEPT_TERMS'
  allowsNews: boolean
}

type SubmitData = {
  type: 'SUBMITTED_DATA'
}

type SaveRegressors = {
  type: 'SAVE_REGRESSORS'
  data: Regressors
}

type SaveValorExacto = {
  type: 'SAVE_VALOR_EXACTO'
  valores_exactos: Etapa1Data['valores_exactos']
  timestamps: Record<string, number>
}

type SaveRango = {
  type: 'SAVE_RANGO'
  rangos: Etapa1Data['rangos']
}

type SaveEtapa2 = {
  type: 'SAVE_ETAPA_2'
  values: Etapa2Values
  order: Etapa2OptionConfig[]
}

type SavePrevAnswers = {
  type: 'SAVE_PREV_ANSWERS'
  plays: Etapa3Data['plays']
}

type SaveEtapa3 = {
  type: 'SAVE_ETAPA_3'
  plays: Etapa3Data['plays']
  fps: number
}

type SaveTimestamp = {
  type: 'SAVE_TIMESTAMP'
  value: number
  key: keyof Experiment['timestamps']
}

type ReducerAction =
  | AcceptTerms
  | Login
  | Visit
  | SubmitData
  | SaveRegressors
  | SaveValorExacto
  | SaveRango
  | SaveEtapa2
  | SaveEtapa3
  | SavePrevAnswers
  | SaveTimestamp

function reducer(state: AppContextData, action: ReducerAction): AppContextData {
  switch (action.type) {
    case 'LOGIN': {
      return {
        ...state,
        auth: true,
        user: {
          ...state.user,
          ...action.user,
        },
      }
    }
    case 'REGISTER_VISIT': {
      return {
        ...state,
        visited: [...state.visited, action.route],
      }
    }
    case 'SAVE_TIMESTAMP': {
      const { key, value } = action
      return {
        ...state,
        experiment: {
          ...state.experiment,
          timestamps: {
            ...state.experiment.timestamps,
            [key]: value - state.experiment.timestamps.inicio_experimento,
          },
        },
      }
    }
    case 'ACCEPT_TERMS': {
      return {
        ...state,
        user: {
          ...state.user!,
          allowsNews: action.allowsNews,
        },
      }
    }
    case 'SUBMITTED_DATA': {
      return {
        ...state,
        submitted: true,
      }
    }
    case 'SAVE_REGRESSORS': {
      const { data } = action
      return {
        ...state,
        regressors: data,
      }
    }
    case 'SAVE_VALOR_EXACTO': {
      const { valores_exactos, timestamps } = action
      return {
        ...state,
        experiment: {
          ...state.experiment,
          etapa_1: {
            ...state.experiment.etapa_1,
            valores_exactos,
            timestamps,
          },
        },
      }
    }
    case 'SAVE_RANGO': {
      const { rangos } = action
      return {
        ...state,
        experiment: {
          ...state.experiment,
          etapa_1: {
            ...state.experiment.etapa_1,
            rangos,
          },
        },
      }
    }
    case 'SAVE_ETAPA_2': {
      const { values, order } = action
      return {
        ...state,
        experiment: {
          ...state.experiment,
          etapa_2: {
            ...state.experiment.etapa_2,
            values,
            order,
          },
        },
      }
    }
    case 'SAVE_PREV_ANSWERS': {
      const { plays } = action
      return {
        ...state,
        experiment: {
          ...state.experiment,
          etapa_3: {
            plays,
            fps: -999,
            width,
            dificultades,
          },
        },
      }
    }
    case 'SAVE_ETAPA_3': {
      const { plays, fps } = action
      return {
        ...state,
        experiment: {
          ...state.experiment,
          etapa_3: {
            plays,
            fps,
            width,
            dificultades,
            puntos: plays.reduce((puntos, play) => {
              const { level_key, others_form } = play
              const [color_ganador] = level_key.split('_')
              const colores = ['azul', 'rojo']
              if (others_form && others_form.color) {
                const i = parseInt(others_form.color) - 1
                return puntos + (colores[i] === color_ganador ? 1 : 0)
              }
              return puntos
            }, 0),
          },
        },
      }
    }
  }
}

export const AppContext = React.createContext({
  appState: initialAppContextData(),
  dispatch: (value: ReducerAction) => {},
})

export function AppContextProvider(props: { children: ReactChild }) {
  const [state, dispatch] = React.useReducer(reducer, initialAppContextData())

  return (
    <AppContext.Provider value={{ appState: state, dispatch }}>
      {props.children}
    </AppContext.Provider>
  )
}
